import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { PostHogProvider as PostHogProviderOriginal, usePostHog } from 'posthog-js/react'
import { useUserQuery } from 'hooks/useApiQuery'
import { useAuth } from 'contexts/auth-context'
import { HostType, getHostType } from 'utils/getHostType'

const postHogOptions = {
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  // Disable all autocapture events. We only use PostHog for feature flags now.
  autocapture: false,
  disable_session_recording: true,
  capture_pageview: false,
}

type Props = {
  children: React.ReactNode
}

/**
 * When user is logged in, add it to PostHog to to target this user in feature flags.
 *
 * PostHog has multiple ids per user, and this will link our internal user id with the PostHog autogenerated
 * user id. After this association, each following requests for the feature flags will be associated
 * with this user. PostHog API usually works faster than our user api, so in most cases will have feature
 * flag ready before user API returns.
 *
 * More info:
 * - https://posthog.com/docs/getting-started/identify-users
 * - https://posthog.com/docs/libraries/js#identifying-users
 */
const PostHogIdentify = () => {
  const { data, isLoading } = useUserQuery()
  const { isSignedIn } = useAuth()
  const posthog = usePostHog()
  const prevIsSignedIn = useRef<boolean>()

  const { id, email, name, lastName } = data || {}

  useEffect(() => {
    if (!isLoading && id) {
      posthog?.identify(id.toString(), {
        id,
        email,
        name,
        lastName,
      })
    }
  }, [posthog, isLoading, id, email, name, lastName])

  // Reset posthog user on logout.
  // https://posthog.com/docs/libraries/js#reset-after-logout
  useEffect(() => {
    if (prevIsSignedIn.current && !isSignedIn) {
      posthog?.reset(true)
      posthog?.reloadFeatureFlags()
    }
    prevIsSignedIn.current = isSignedIn
  }, [posthog, isSignedIn])

  return null
}

export const postHogApiKey = () => {
  if (getHostType() === HostType.PROD && process.env.REACT_APP_PUBLIC_POSTHOG_KEY_PROD != null) {
    return process.env.REACT_APP_PUBLIC_POSTHOG_KEY_PROD
  }

  return process.env.REACT_APP_PUBLIC_POSTHOG_KEY
}

export const PostHogContext = createContext<{ isError: boolean }>({ isError: false })

export const usePostHogContext = () => useContext(PostHogContext)

/**
 * Connect PostHog for feature flags and associate logged in user with it.
 * You can use `useFeatureFlagEnabled` hook to access feature flags as described here:
 * https://posthog.com/docs/libraries/react#feature-flags
 */
export const PostHogProvider = ({ children }: Props) => {
  const [isError, setIsError] = useState(false)
  const options = {
    ...postHogOptions,
    on_xhr_error: (req: XMLHttpRequest) => {
      setIsError(true)
      throw new Error(`PostHog XHR error ${req?.responseText}`)
    },
  }

  return (
    <PostHogContext.Provider value={{ isError }}>
      <PostHogProviderOriginal apiKey={postHogApiKey()} options={options}>
        <PostHogIdentify />
        {children}
      </PostHogProviderOriginal>
    </PostHogContext.Provider>
  )
}
